import React, { useEffect, useRef, useState } from 'react';
import CoachMessageInput, { useCoachComposerSubmit } from '../CoachMessageInput';

const MAX_IMAGES = 2;

function ExerciseCoachComposer({
  onSubmit,
  disabled = false,
  placeholder = 'Ask coach...',
  resetKey = null,
  draftRequest = null,
}) {
  const fileInputRef = useRef(null);
  const textInputRef = useRef(null);
  const [message, setMessage] = useState('');
  const [files, setFiles] = useState([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState('');

  useEffect(() => {
    setMessage('');
    setFiles([]);
    setSubmitError('');
  }, [resetKey]);

  useEffect(() => {
    if (!draftRequest?.key) return;
    setMessage(String(draftRequest.text || ''));
    textInputRef.current?.focus();
  }, [draftRequest?.key, draftRequest?.text]);

  const removeFile = (index) => {
    setFiles(current => current.filter((_, itemIndex) => itemIndex !== index));
  };

  const handleFilesSelected = (event) => {
    const selected = Array.from(event.target.files || [])
      .filter(file => file.type.startsWith('image/'));
    setFiles(current => [...current, ...selected].slice(0, MAX_IMAGES));
    event.target.value = '';
  };

  const canSend = Boolean(message.trim() || files.length > 0) && !disabled && !isSubmitting;
  const submit = async () => {
    if (!canSend || !onSubmit) return;
    const submittedMessage = message.trim();
    const submittedFiles = files.slice();
    setIsSubmitting(true);
    setSubmitError('');
    try {
      await onSubmit(submittedMessage, submittedFiles);
      setMessage('');
      setFiles([]);
    } catch (error) {
      if (error?.messageSaved) {
        setMessage('');
        setFiles([]);
      }
      setSubmitError(error?.message || 'Could not send this message. Try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const submitComposer = useCoachComposerSubmit(submit, disabled || isSubmitting);

  return (
    <div className="exercise-coach-composer">
      {files.length > 0 && (
        <div className="exercise-coach-pending-attachments" aria-label="Pending attachments">
          {files.map((file, index) => (
            <div className="exercise-coach-pending-attachment" key={`${file.name}-${file.size}-${index}`}>
              <span className="exercise-coach-pending-attachment-name">{file.name}</span>
              <button
                type="button"
                className="exercise-coach-remove-attachment"
                aria-label={`Remove ${file.name}`}
                onClick={() => removeFile(index)}
                disabled={disabled || isSubmitting}
              >
                ×
              </button>
            </div>
          ))}
        </div>
      )}
      {submitError && (
        <div className="exercise-coach-composer-error" role="alert">{submitError}</div>
      )}
      <div className="chat-input-container">
        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          multiple
          className="exercise-coach-file-input"
          aria-label="Choose photos for Coach"
          onChange={handleFilesSelected}
          disabled={disabled || isSubmitting || files.length >= MAX_IMAGES}
        />
        <button
          type="button"
          className="exercise-coach-attach-btn"
          aria-label="Attach photos for Coach"
          title="Attach photos"
          onClick={() => fileInputRef.current?.click()}
          disabled={disabled || isSubmitting || files.length >= MAX_IMAGES}
        >
          📎
        </button>
        <CoachMessageInput
          ref={textInputRef}
          className="chat-input"
          value={message}
          onChange={(event) => setMessage(event.target.value)}
          placeholder={placeholder}
          disabled={disabled || isSubmitting}
          onSend={submitComposer}
        />
        <button
          type="button"
          className="chat-send-btn"
          aria-label="Send message to Coach"
          onClick={submitComposer}
          disabled={!canSend}
        >
          {isSubmitting ? 'Sending…' : 'Send'}
        </button>
      </div>
    </div>
  );
}

export default ExerciseCoachComposer;
